import React, { useState, useEffect } from "react";
import { Table, Form, Button, Pagination, Modal, Badge } from "react-bootstrap";
import axios from "axios";
import Layout from "../../../components/Layouts/Layouts/Layout";
import DashboardHeader from "../../../components/basic/DashboardHeader";
import { getURLbyEndPoint } from "../../../store/api";

const getEmailTemplate = (companyName, validUpTo) => {
  const expiryDate = validUpTo
    ? new Date(validUpTo.replace(/-/g, "/")).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "N/A";

  return `
        <!DOCTYPE html>
        <html lang="en">
        <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
            body {
            margin: 0;
            padding: 0;
            font-family: "Helvetica", sans-serif;
            background-color: #f4f4f4;
            }
        </style>
        </head>
        <body>       
        <p>Dear Esteemed Member,</p>
        <p>Greetings from AECCI!</p>
        <p>This is a kind reminder that your AECCI membership renewal is due on <strong>${expiryDate}</strong>. We request you to renew your membership to continue enjoying uninterrupted access to our exclusive services and benefits.</p>

        <p><strong>AECCI membership offers you:</strong></p>
        <p>
            <span>• Seamless access to our e-attestation platform for export documentation including invoices, packing lists, bills of lading, fumigation certificates, veterinary certificates, certificates of analysis, phytosanitary and health certificates, and Certificates of Origin (Non-Preferential) via DGFT.</span><br>
            <span>• Access to our B2B global trade network, connecting you with verified partners across 50+ countries.</span><br>
            <span>• Support from AECCI-IAC (International Arbitration Centre) for effective and professional resolution of trade disputes.</span><br>
            <span>• Participation in our regular webinars, workshops, and knowledge sessions, designed to educate and empower the Indian EXIM community with the latest global trade updates and compliance practices.</span>
        </p>

        <p>🔗 To learn more about membership benefits:<br>
        <a href="https://www.aecci.org.in/membership-and-its-benefits/">https://www.aecci.org.in/membership-and-its-benefits/</a></p>

        <p><strong>Convenient Payment Options</strong><br>
        You can now renew your membership easily:</p>

        <p><strong>Renew Online:</strong><br>
        You can simply login using <a href="https://e-platform.aecci.org.in/login">https://e-platform.aecci.org.in/login</a> and under <a href="https://e-platform.aecci.org.in/membership-services">https://e-platform.aecci.org.in/membership-services</a> you can simply renew your membership.</p>

        <p>Also you can do manual payment using the below bank details and update your wallet:</p>

        <p>
            <strong>Bank Transfer Details:</strong><br>
            <span>Account Name: ASIAN EXPORTERS CHAMBER OF COMMERCE AND INDUSTRY</span><br>
            <span>Account No.: 021688700000780</span><br>
            <span>IFSC Code: YESB0000216</span><br>
            <span>Branch: CBD Belapur</span>
        </p>

        <p>We encourage you to renew at the earliest to avoid any interruption in services.</p>
        <p>Should you require any assistance, please feel free to contact us. We remain committed to supporting your business growth and international trade success.</p>
        <p>Thank you for your continued association with AECCI.</p>

        <p>
            <span>Warm regards,</span><br>
            <span><strong>AECCI Team</strong></span><br>
            <span>Asian Exporters' Chamber of Commerce & Industry</span>
        </p>
        </body>
        </html>
    `;
};

const MembershipExpiryTracker = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [membershipFilter, setMembershipFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  const [sendingEmail, setSendingEmail] = useState({});

  // Email preview modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailModalData, setEmailModalData] = useState(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        getURLbyEndPoint("getMembershipExpiryList")
      );
      if (response.data.status) {
        setMembers(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Get unique membership types for filter
  const membershipTypes = [
    ...new Set(members.map((m) => m.selectMembership)),
  ].sort();

  // Filtering
  const filteredData = members.filter((data) => {
    if (statusFilter && data.status !== statusFilter) return false;
    if (membershipFilter && data.selectMembership !== membershipFilter)
      return false;
    if (
      searchTerm &&
      !data.companyName.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !(data.memberShipNo || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    )
      return false;
    return true;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentFiltered = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handlePrevPage = () =>
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));



  // Open email preview
  const handleOpenEmailPreview = (member) => {
    setEmailModalData(member);
    setEmailSubject(
      "Reminder: AECCI Membership Renewal Due – Continue Your Trade Benefits"
    );
    setEmailBody(getEmailTemplate(member.companyName, member.validUpTo));
    setShowEmailModal(true);
  };

  // Send email
  const handleSendEmail = async () => {
    if (!emailModalData) return;

    setSendingEmail((prev) => ({ ...prev, [emailModalData._id]: true }));
    try {
      const res = await axios.post(
        getURLbyEndPoint("sendMembershipRenewalEmail") + emailModalData._id,
        {
          subject: emailSubject,
          emailBody: emailBody,
        }
      );
      if (res.data.status) {
        alert(res.data.message);
        setShowEmailModal(false);
        fetchData(); // Refresh the list to show the new email sent status
      } else {
        alert(res.data.message || "Failed to send email");
      }
    } catch (error) {
      console.error(error);
      alert("Failed to send email");
    } finally {
      setSendingEmail((prev) => ({ ...prev, [emailModalData._id]: false }));
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Expired":
        return <Badge bg="danger">Expired</Badge>;
      case "Expiring Soon":
        return <Badge bg="warning" text="dark">Expiring Soon</Badge>;
      case "Active":
        return <Badge bg="success">Active</Badge>;
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const getRenewalBadge = (renewal) => {
    if (!renewal) return <span className="text-muted">—</span>;
    switch (renewal.isApproved) {
      case "Processing":
        return <Badge bg="info">Processing</Badge>;
      case "Updated":
        return <Badge bg="success">Renewed</Badge>;
      case "Rejected":
        return <Badge bg="danger">Rejected</Badge>;
      default:
        return <Badge bg="secondary">{renewal.isApproved}</Badge>;
    }
  };

  // Stats
  const expiredCount = members.filter((m) => m.status === "Expired").length;
  const expiringSoonCount = members.filter(
    (m) => m.status === "Expiring Soon"
  ).length;
  const activeCount = members.filter((m) => m.status === "Active").length;

  return (
    <>
      <Layout>
        <div className="d-flex justify-content-center align-items-start">
          <div className="py-1 align-items-center justify-content-center main-width">
            <hr />
            <DashboardHeader
              heading="Services / Membership Renewal mails"
              backUrl="/master-membership-services"
            />
            <hr />

            {/* Stats Cards */}
            <div className="d-flex gap-3 mb-3 flex-wrap">
              <div
                className="p-3 rounded text-center flex-fill"
                style={{
                  background: "#f8d7da",
                  border: "1px solid #f5c6cb",
                  minWidth: "150px",
                }}
              >
                <h4 className="mb-0 text-danger">{expiredCount}</h4>
                <small className="text-danger fw-semibold">Expired</small>
              </div>
              <div
                className="p-3 rounded text-center flex-fill"
                style={{
                  background: "#fff3cd",
                  border: "1px solid #ffeeba",
                  minWidth: "150px",
                }}
              >
                <h4 className="mb-0" style={{ color: "#856404" }}>
                  {expiringSoonCount}
                </h4>
                <small className="fw-semibold" style={{ color: "#856404" }}>
                  Expiring Soon (≤30 days)
                </small>
              </div>
              <div
                className="p-3 rounded text-center flex-fill"
                style={{
                  background: "#d4edda",
                  border: "1px solid #c3e6cb",
                  minWidth: "150px",
                }}
              >
                <h4 className="mb-0 text-success">{activeCount}</h4>
                <small className="text-success fw-semibold">Active</small>
              </div>
              <div
                className="p-3 rounded text-center flex-fill"
                style={{
                  background: "#e2e3e5",
                  border: "1px solid #d6d8db",
                  minWidth: "150px",
                }}
              >
                <h4 className="mb-0">{members.length}</h4>
                <small className="fw-semibold">Total Members</small>
              </div>
            </div>

            {/* Filters */}
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <div className="d-flex gap-2 flex-wrap">
                <Form.Check
                  inline
                  label="All"
                  name="statusFilter"
                  type="radio"
                  checked={statusFilter === ""}
                  onChange={() => {
                    setStatusFilter("");
                    setCurrentPage(1);
                  }}
                />
                <Form.Check
                  inline
                  label="Expired"
                  name="statusFilter"
                  type="radio"
                  checked={statusFilter === "Expired"}
                  onChange={() => {
                    setStatusFilter("Expired");
                    setCurrentPage(1);
                  }}
                />
                <Form.Check
                  inline
                  label="Expiring Soon"
                  name="statusFilter"
                  type="radio"
                  checked={statusFilter === "Expiring Soon"}
                  onChange={() => {
                    setStatusFilter("Expiring Soon");
                    setCurrentPage(1);
                  }}
                />
                <Form.Check
                  inline
                  label="Active"
                  name="statusFilter"
                  type="radio"
                  checked={statusFilter === "Active"}
                  onChange={() => {
                    setStatusFilter("Active");
                    setCurrentPage(1);
                  }}
                />
              </div>
              <div className="d-flex gap-2 flex-wrap">
                <Form.Select
                  size="sm"
                  value={membershipFilter}
                  onChange={(e) => {
                    setMembershipFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{ width: "200px" }}
                >
                  <option value="">All Memberships</option>
                  {membershipTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Form.Select>
                <input
                  className="form-control form-control-sm"
                  type="search"
                  placeholder="Search company or membership no."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{ width: "250px" }}
                />
              </div>
            </div>

            <p className="text-muted mb-2">
              Showing {filteredData.length} of {members.length} members
            </p>

            {/* Table */}
            {loading ? (
              <p>Loading...</p>
            ) : members.length > 0 ? (
              <Table
                responsive
                striped
                bordered
                hover
                className="text-center"
                size="sm"
              >
                <thead>
                  <tr>
                    <th className="align-middle">SR</th>
                    <th className="align-middle">Company Name</th>
                    <th className="align-middle">Membership No.</th>
                    <th className="align-middle">Type</th>
                    <th className="align-middle">Valid Upto</th>
                    <th className="align-middle">Days Left</th>
                    <th className="align-middle">Status</th>
                    <th className="align-middle">Renewal</th>
                    <th className="align-middle">Email</th>
                    <th className="align-middle">Reminder</th>
                    <th className="align-middle">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentFiltered.length > 0 ? (
                    currentFiltered.map((data, index) => (
                      <tr key={data._id}>
                        <td>{indexOfFirstItem + index + 1}</td>
                        <td className="text-start" style={{ maxWidth: "200px" }}>
                          <small className="fw-semibold">{data.companyName}</small>
                        </td>
                        <td>
                          <small>{data.memberShipNo || "—"}</small>
                        </td>
                        <td>
                          <small>{data.selectMembership}</small>
                        </td>
                        <td>
                          <small>
                            {data.validUpTo
                              ? new Date(
                                  data.validUpTo.replace(/-/g, "/")
                                ).toLocaleDateString("en-GB")
                              : "—"}
                          </small>
                        </td>
                        <td>
                          <small
                            className={
                              data.daysLeft <= 0
                                ? "text-danger fw-bold"
                                : data.daysLeft <= 30
                                ? "text-warning fw-bold"
                                : "text-success"
                            }
                          >
                            {data.daysLeft !== null ? data.daysLeft : "—"}
                          </small>
                        </td>
                        <td>{getStatusBadge(data.status)}</td>
                        <td>{getRenewalBadge(data.latestRenewal)}</td>
                        <td>
                          <small>{data.email}</small>
                        </td>
                        <td>
                          {data.latestEmail ? (
                            <div className="d-flex flex-column align-items-center">
                              <Badge bg="success" className="mb-1">
                                Sent
                              </Badge>
                              <small style={{ fontSize: "10px", color: "#666" }}>
                                {new Date(data.latestEmail.sentAt).toLocaleDateString("en-GB")}
                              </small>
                            </div>
                          ) : (
                            <Badge bg="secondary">Not Sent</Badge>
                          )}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="primary"
                            disabled={sendingEmail[data._id]}
                            onClick={() => handleOpenEmailPreview(data)}
                          >
                            {sendingEmail[data._id]
                              ? "Sending..."
                              : "📧 Send"}
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={11} className="text-center">
                        No Matching Data
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            ) : (
              <p>No data available</p>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-start mt-2">
                <Pagination size="sm">
                  <Pagination.Prev
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                  />
                  {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                    const startPage = Math.max(
                      1,
                      Math.min(currentPage - 5, totalPages - 9)
                    );
                    const page = startPage + i;
                    if (page > totalPages) return null;
                    return (
                      <Pagination.Item
                        key={page}
                        active={page === currentPage}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </Pagination.Item>
                    );
                  })}
                  <Pagination.Next
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </div>
            )}
          </div>
        </div>
      </Layout>

      {/* Email Preview Modal */}
      <Modal
        show={showEmailModal}
        onHide={() => setShowEmailModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Email Preview — {emailModalData?.companyName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex gap-3 mb-3">
            <div className="flex-fill">
              <Form.Label className="fw-semibold">To:</Form.Label>
              <Form.Control type="text" value={emailModalData?.email || ""} disabled size="sm" />
            </div>
            <div className="flex-fill">
              <Form.Label className="fw-semibold">CC:</Form.Label>
              <Form.Control type="text" value="ed@aecci.org.in" disabled size="sm" />
            </div>
            <div className="flex-fill">
              <Form.Label className="fw-semibold">From:</Form.Label>
              <Form.Control type="text" value="membership@aecci.org.in" disabled size="sm" />
            </div>
          </div>
          <div className="mb-3">
            <Form.Label className="fw-semibold">Subject:</Form.Label>
            <Form.Control
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
            />
          </div>
          <div className="mb-3">
            <Form.Label className="fw-semibold">Preview:</Form.Label>
            <div
              contentEditable
              suppressContentEditableWarning
              onBlur={(e) => setEmailBody(e.currentTarget.innerHTML)}
              style={{
                border: "1px solid #ddd",
                borderRadius: "8px",
                padding: "16px",
                maxHeight: "400px",
                overflow: "auto",
                background: "#fff",
              }}
              dangerouslySetInnerHTML={{ __html: emailBody }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowEmailModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleSendEmail}
            disabled={sendingEmail[emailModalData?._id]}
          >
            {sendingEmail[emailModalData?._id]
              ? "Sending..."
              : "✉️ Send Email"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MembershipExpiryTracker;
